import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import AuthLayout from '../components/AuthLayout';
import assetService, { Asset } from '../services/asset.service';
import MapComponent from '../components/MapComponent';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AssetDetail: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAsset = async () => {
      try {
        setLoading(true);
        const response = await assetService.getAssetById(assetId!);
        
        if (response.error) {
          setError(response.error);
          return;
        }
        
        if (response.asset) {
          setAsset(response.asset);
        } else {
          setError('Ativo não encontrado');
        }
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar dados do ativo');
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [assetId]);

  const handleDelete = async () => {
    if (window.confirm('Tem certeza que deseja excluir este ativo?')) {
      try {
        await assetService.deleteAsset(assetId!);
        navigate('/assets');
      } catch (err: any) {
        setError(err.message || 'Erro ao excluir ativo');
      }
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Detalhes do Ativo">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (error) {
    return (
      <AuthLayout title="Detalhes do Ativo">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assets')}
        >
          Voltar para Lista
        </Button>
      </AuthLayout>
    );
  }

  if (!asset) {
    return (
      <AuthLayout title="Detalhes do Ativo">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ativo não encontrado
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assets')}
        >
          Voltar para Lista
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={`Ativo: ${asset.name}`}>
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/assets')}
          sx={{ mr: 1 }}
        >
          Voltar
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/assets/edit/${assetId}`)}
          sx={{ mr: 1 }}
        >
          Editar
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
        >
          Excluir
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, minHeight: 600, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Informações Gerais
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Nome
                  </Typography>
                  <Typography variant="body1">{asset.name}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Categoria
                  </Typography>
                  <Chip 
                    icon={<CategoryIcon />} 
                    // @ts-ignore - Propriedade name não existe no tipo string
                    label={asset.category?.name || 'Sem categoria'} 
                    variant="outlined" 
                    size="small" 
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Cliente
                  </Typography>
                  <Typography variant="body1">
                    {/* @ts-ignore - Propriedade name não existe no tipo string */}
                    {asset.client?.name || 'Sem cliente associado'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Modelo
                  </Typography>
                  <Typography variant="body1">{asset.model || 'Não especificado'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Número de Série
                  </Typography>
                  <Typography variant="body1">{asset.serialNumber || 'Não especificado'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Data de Instalação
                  </Typography>
                  <Typography variant="body1">
                    {/* @ts-ignore - Propriedade installationDate não existe no tipo Asset */}
                    {(asset as any).installationDate 
                      ? format(new Date((asset as any).installationDate), 'dd/MM/yyyy', { locale: ptBR })
                      : 'Não especificada'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Descrição
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {asset.description || 'Sem descrição'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, minHeight: 600, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Localização
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Exibir endereço se disponível */}
            {asset.address && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Endereço
                </Typography>
                <Typography variant="body1">
                  {asset.address.street}, {asset.address.number}
                  {asset.address.complement && `, ${asset.address.complement}`}
                </Typography>
                <Typography variant="body2">
                  {asset.address.neighborhood}, {asset.address.city} - {asset.address.state}
                </Typography>
                <Typography variant="body2">
                  CEP: {asset.address.zipCode}
                </Typography>
              </Box>
            )}

            {/* Exibir coordenadas GPS se disponíveis */}
            {/* @ts-ignore - Verificar latitude e longitude */}
            {((asset as any).latitude && (asset as any).longitude) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Coordenadas GPS
                </Typography>
                <Typography variant="body2">
                  Latitude: {(asset as any).latitude}° | Longitude: {(asset as any).longitude}°
                </Typography>
              </Box>
            )}

            {/* Exibir mapa - priorizar latitude/longitude diretas, depois address.coordinates */}
            {/* @ts-ignore */}
            {(asset as any).latitude && (asset as any).longitude ? (
              <Box sx={{ height: 400, width: '100%', mt: 2, borderRadius: 1, overflow: 'hidden' }}>
                <MapComponent 
                  latitude={Number((asset as any).latitude)}
                  longitude={Number((asset as any).longitude)}
                  popupText={asset.name}
                />
              </Box>
            ) : asset.address?.coordinates?.lat && asset.address?.coordinates?.lng ? (
              <Box sx={{ height: 400, width: '100%', mt: 2, borderRadius: 1, overflow: 'hidden' }}>
                <MapComponent 
                  latitude={Number(asset.address.coordinates.lat)}
                  longitude={Number(asset.address.coordinates.lng)}
                  popupText={asset.name}
                />
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Nenhuma coordenada GPS disponível para exibir no mapa
              </Typography>
            )}
          </Paper>
        </Grid>

        {asset.photos && asset.photos.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Fotos
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {asset.photos.map((photo, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={photo.url}
                        alt={`Foto ${index + 1} do ativo`}
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {photo.description || `Foto ${index + 1}`}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </AuthLayout>
  );
};

export default AssetDetail;